import type { FieldDef } from "@/api/types/FieldDef";
import type { FieldValue } from "@/api/types/FieldValue";
import type { StatusCategory } from "@/api/types/StatusCategory";
import type { StatusColor } from "@/api/types/StatusColor";
import type { TaskPriority } from "@/api/types/TaskPriority";
import type { TaskStatus } from "@/api/types/TaskStatus";
import type { TaskSummary } from "@/api/types/TaskSummary";

export const taskUrl = (t: { project_key: string; key: string }) => `/p/${t.project_key}/tasks/${t.key}`;

export const isOpen = (c: StatusCategory) => c === "not_started" || c === "active";

// ───────────── Priority ─────────────

/** Most urgent first. `null` (no priority) comes after all of them. */
export const PRIORITIES: TaskPriority[] = ["urgent", "high", "medium", "low"];

/** 0 = urgent … 3 = low, 4 = no priority. */
export const priorityRank = (p: TaskPriority | null) => (p ? PRIORITIES.indexOf(p) : PRIORITIES.length);

export function comparePriority(a: Pick<TaskSummary, "priority">, b: Pick<TaskSummary, "priority">): number {
  return priorityRank(a.priority) - priorityRank(b.priority);
}

// ───────────── Due dates ─────────────
// Due dates are instants. Days are the viewer's own: "today" is today where they are.

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
export const addDays = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n, d.getHours(), d.getMinutes());

/** Whole days from today to `iso` (0 = today, -1 = yesterday). */
export function dayOffset(iso: string, now = new Date()): number {
  return Math.round((startOfDay(new Date(iso)).getTime() - startOfDay(now).getTime()) / 86_400_000);
}

export type DueBucket = "overdue" | "today" | "tomorrow" | "week" | "later" | "none";

export function dueBucket(t: Pick<TaskSummary, "due_at" | "is_overdue">, now = new Date()): DueBucket {
  if (!t.due_at) return "none";
  if (t.is_overdue) return "overdue";
  const d = dayOffset(t.due_at, now);
  if (d <= 0) return "today";
  if (d === 1) return "tomorrow";
  if (d < 7) return "week";
  return "later";
}

/** `<input type="datetime-local">` value for an instant, in the viewer's zone. */
export function toLocalInput(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function fromLocalInput(v: string): string | null {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** End of the viewer's day `offset` days from today, as an instant: the default for "due tomorrow". */
export function endOfDay(offset: number, now = new Date()): string {
  const d = startOfDay(now);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + offset, 18, 0).toISOString();
}

// ───────────── Grouping ─────────────

export type GroupBy = "status" | "assignee" | "due" | "priority" | "project" | "sprint" | "none" | `field:${string}`;

export type Group = {
  id: string;
  label: string;
  category?: StatusCategory;
  /** Grouped by status: its chosen colour (`null` = its category's). */
  color?: StatusColor | null;
  /** Grouped by priority: this group's level (`null` = no priority). */
  priority?: TaskPriority | null;
  tasks: TaskSummary[];
};

const DUE_ORDER: DueBucket[] = ["overdue", "today", "tomorrow", "week", "later", "none"];

/** Groups in display order. Status groups follow the project's order and include empty ones. */
export function groupTasks(
  tasks: TaskSummary[],
  by: GroupBy,
  ctx: { statuses?: TaskStatus[]; fields?: FieldDef[]; t: (k: string) => string },
): Group[] {
  if (by === "none") return [{ id: "all", label: "", tasks }];
  if (by === "status") {
    const known = (ctx.statuses ?? []).map<Group>((s) => ({ id: s.id, label: s.name, category: s.category, color: s.color, tasks: [] }));
    const map = new Map(known.map((g) => [g.id, g]));
    for (const t of tasks) {
      let g = map.get(t.status.id);
      if (!g) {
        g = { id: t.status.id, label: t.status.name, category: t.status.category, tasks: [] };
        map.set(g.id, g);
        known.push(g);
      }
      g.tasks.push(t);
    }
    return known;
  }
  if (by === "due") {
    const groups = DUE_ORDER.map<Group>((b) => ({ id: b, label: ctx.t(`work.dueBucket.${b}`), tasks: [] }));
    for (const t of tasks) groups[DUE_ORDER.indexOf(dueBucket(t))]!.tasks.push(t);
    return groups.filter((g) => g.tasks.length > 0);
  }
  if (by === "priority") {
    const groups = [...PRIORITIES, null].map<Group>((p) => ({ id: p ?? "none", label: ctx.t(`work.priorities.${p ?? "none"}`), priority: p, tasks: [] }));
    for (const t of tasks) groups[priorityRank(t.priority)]!.tasks.push(t);
    return groups.filter((g) => g.tasks.length > 0);
  }
  if (by === "project") {
    const map = new Map<string, Group>();
    for (const t of tasks) {
      if (!map.has(t.project_key)) map.set(t.project_key, { id: t.project_key, label: t.project_key, tasks: [] });
      map.get(t.project_key)!.tasks.push(t);
    }
    return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
  }
  if (by === "sprint") {
    // Active first, then planned in order (the list comes sorted by sprint when asked), the backlog last.
    const map = new Map<string, Group & { rank: number }>();
    const none: Group = { id: "none", label: ctx.t("sprints.backlog"), tasks: [] };
    const rank = { active: 0, planned: 1, completed: 2 } as const;
    for (const t of tasks) {
      if (!t.sprint || t.sprint.state === "completed") {
        none.tasks.push(t);
        continue;
      }
      const s = t.sprint;
      if (!map.has(s.id)) map.set(s.id, { id: s.id, label: s.name, tasks: [], rank: rank[s.state] * 100_000 + s.number });
      map.get(s.id)!.tasks.push(t);
    }
    const out: Group[] = [...map.values()].sort((a, b) => a.rank - b.rank).map(({ rank: _r, ...g }) => g);
    return none.tasks.length ? [...out, none] : out;
  }
  if (by === "assignee") {
    const map = new Map<string, Group>();
    const none: Group = { id: "none", label: ctx.t("work.unassigned"), tasks: [] };
    for (const t of tasks) {
      if (!t.assignees.length) none.tasks.push(t);
      for (const a of t.assignees) {
        if (!map.has(a.id)) map.set(a.id, { id: a.id, label: a.display_name, tasks: [] });
        map.get(a.id)!.tasks.push(t);
      }
    }
    const out = [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
    return none.tasks.length ? [...out, none] : out;
  }
  const fieldId = by.slice("field:".length);
  const field = ctx.fields?.find((f) => f.id === fieldId);
  const map = new Map<string, Group>();
  if (field?.field_type === "single_select") for (const o of field.options) map.set(o.id, { id: o.id, label: o.name, tasks: [] });
  const none: Group = { id: "none", label: ctx.t("work.noValue"), tasks: [] };
  for (const t of tasks) {
    const v = t.field_values.find((x) => x.field_id === fieldId);
    if (!v || !field) {
      none.tasks.push(t);
      continue;
    }
    const id = String(v.value);
    if (!map.has(id)) map.set(id, { id, label: fieldText(field, v), tasks: [] });
    map.get(id)!.tasks.push(t);
  }
  const out = [...map.values()].filter((g) => g.tasks.length > 0 || field?.field_type === "single_select");
  return none.tasks.length ? [...out, none] : out;
}

// ───────────── Custom field values ─────────────

/** A value as text, for a cell or a chip. */
export function fieldText(field: FieldDef, v: FieldValue | undefined): string {
  if (!v) return "";
  switch (field.field_type) {
    case "single_select":
      return field.options.find((o) => o.id === v.value)?.name ?? "";
    case "checkbox":
      return v.value ? "✓" : "✗";
    case "date":
      return new Date(String(v.value)).toLocaleDateString();
    case "user":
      return v.user?.display_name ?? "";
    case "number":
      return String(v.value);
    default:
      return String(v.value ?? "");
  }
}

export const optionColor = (field: FieldDef, id: unknown) => field.options.find((o) => o.id === id)?.color ?? null;

/** The tag/option palette. Readable on both themes as a soft background with the colour as text. */
export const PALETTE = ["#2752ef", "#0e9f6e", "#d97706", "#dc2626", "#7c3aed", "#db2777", "#0891b2", "#4b5563"];

// ───────────── Sorting (client side, for views that re-sort what they hold) ─────────────

export function compareDue(a: TaskSummary, b: TaskSummary): number {
  if (!a.due_at && !b.due_at) return 0;
  if (!a.due_at) return 1;
  if (!b.due_at) return -1;
  return a.due_at.localeCompare(b.due_at);
}
